import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Juniorpod's Easify live preview is attached to the *existing* gallery.
        // Replacing that custom element deletes the app's preview DOM and listeners
        // whenever a colour/size variant is selected.  Keep the gallery mounted
        // and update only Shopify's underlying shirt image on this one product.
        if (this.dataset.juniorpodCreator === 'true') {
          this.#refreshJuniorpodShirtImage(newMediaGallery);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Keep Easify's personalised-design layer mounted while Shopify changes the
   * selected shirt colour.  The selected variant is the first media in the
   * newly rendered product gallery.  We replace ONLY the stock-image source:
   * no app nodes, canvas, preview overlays or event handlers are destroyed.
   *
   * Shopify's variant picker, product form, inventory and product-price
   * listeners still run their normal update path without interference.
   *
   * @param {Element} newGallery - Updated product gallery from section HTML.
   */
  #refreshJuniorpodShirtImage(newGallery) {
    // Horizon renders both a carousel and, for a grid presentation, a separate
    // desktop media list. Synchronising only querySelector()'s first match
    // leaves the visible desktop T-shirt on the previous colour.
    const selectors = [
      'slideshow-component slideshow-slide .product-media img.product-media__image',
      '.media-gallery__grid .product-media img.product-media__image',
    ];
    let updated = false;

    for (const selector of selectors) {
      const currentImage = this.querySelector(selector);
      const nextImage = newGallery.querySelector(selector);
      if (!currentImage || !nextImage) continue;

      // Keep the existing slideshow, gallery and Easify Konva canvas mounted.
      // Update only the underlying Shopify product image and its media identity.
      const currentMedia = currentImage.closest('.product-media');
      const nextMedia = nextImage.closest('.product-media');
      if (currentMedia && nextMedia) {
        for (const attr of ['data-media-id', 'style']) {
          const value = nextMedia.getAttribute(attr);
          if (value !== null && currentMedia.getAttribute(attr) !== value) {
            currentMedia.setAttribute(attr, value);
          }
        }
      }

      for (const attr of ['srcset', 'sizes', 'data-max-resolution', 'alt']) {
        const nextValue = nextImage.getAttribute(attr);
        if (nextValue === null) currentImage.removeAttribute(attr);
        else if (currentImage.getAttribute(attr) !== nextValue) {
          currentImage.setAttribute(attr, nextValue);
        }
      }
      const nextSrc = nextImage.getAttribute('src');
      if (nextSrc && currentImage.getAttribute('src') !== nextSrc) {
        currentImage.setAttribute('src', nextSrc);
        updated = true;
      }
    }

    // A layout event helps Easify recompute the preview position after a
    // garment switch. The non-destructive refresh does not rewrite app images.
    if (updated) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }

    // Keep the selected T-shirt/preview at the start of the gallery.
    const slideshow = this.slideshow;
    if (slideshow && typeof slideshow.select === 'function') {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
